import { InvisibleButton } from "../Buttons/Buttons";
import type {
  TagClassificationConfig,
  Filters,
  ProductFiltersMainSchema,
  SupportedLanguage,
  IPresetFilter,
  IFilters,
  IPresetDataItem,
  WithPagination,
} from "../../types/types";
import type {
  ProductStatusesSchema,
  AttributeTemplateSummarySchema,
  ProductStatusType,
} from "../../types/types.PIM";
import * as React from "react";
import { useEffect, useRef, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import useSWR from "swr";
import { ArrayParam, StringParam, useQueryParams } from "use-query-params";
import type { ChipType } from "../Chips/Chips";
import { endpoints } from "../../endpoints";
import {
  convertToChipArray,
  getCustomLabel,
  useStoreState,
} from "../../util/util";
import { useCookies } from "react-cookie";
import noop from "lodash/noop";
import { FilterGroup } from "./FilterGroup";
import { XIcon } from "../Icons/Icons";
import { SearchBar } from "../SearchBar/SearchBar";
import {
  applyFiltersToURL,
  groupPortfolioParamsByKey,
} from "../../pages/public";
import { useHistory, useLocation } from "react-router-dom";
import { screenSize } from "../../theme";
import { stringify } from "querystring";
import { PresetFilter } from "./PresetFilter";
import { useAuthContext } from "../Auth";
import type { AxiosError } from "axios";
import axios from "axios";
import { Notifications } from "../Notifications/NotificationsContext";
import { getProductStatusText, getProductStatusValue } from "../Status/Status";

export function chipsToStringArray(chips: ChipType[]) {
  return chips.map((chip) => chip.name);
}

interface IFiltersFlyBarWrapperProps {
  show: boolean;
}

interface IsearchFilterItem {
  type: string;
  label: string;
}

const FiltersFlyBarWrapper = styled.div<IFiltersFlyBarWrapperProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: ${({ show }) => (show ? "100%" : "0")};
  height: 100%;
  @media ${screenSize.medium} {
    position: ${({ show }) => (show ? "sticky" : "absolute")};
    top: 1px;
    height: calc(100vh - 141px);
    background: ${({ theme }) => theme.primaryBG};
    z-index: 3;
  }
  @media ${screenSize.small} {
    width: 100%;
  }
`;

const FiltersHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.resolve("color_filterSearchBar_border") ?? theme.primaryBorder};
  background: ${({ theme }) => theme.resolve("color_filterSearchBar_bg")};
  padding: ${({ theme }) =>
    ` ${theme.resolve("spacing_lg")} ${theme.resolve("spacing_xl")}`};
  position: sticky;
  top: 0;
  z-index: 3;
`;

const FiltersHeader = styled.div`
  display: flex;
  flex-direction: row;
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.resolve("font_weight_medium")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  justify-content: space-between;
  align-items: center;
  button {
    display: flex;
    padding: 5px;
    background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  }
  svg {
    transform: rotate(90deg);
    &:hover {
      transform: scale(1.2);
    }
  }
`;

const SearchBarWrapper = styled.div`
  position: relative;
  input {
    border-radius: 4px;
    background: ${({ theme }) => theme.primaryBG};
  }
  svg {
    left: 7px;
    path {
      fill: #000;
    }
  }
`;

const FiltersWrapper = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  }
`;

const EmptyFiltersPlaceholder = styled.div`
  width: 100%;
  height: 30px;
  margin: 0 0 35px;
`;

const SearchResultsWrapper = styled.div`
  position: absolute;
  top: 40px;
  left: 0px;
  max-height: 300px;
  overflow: auto;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.primaryBorder};
  border-radius: 4px;
  box-shadow: 2px 2px 2px #00000020;
  background: ${({ theme }) => theme.primaryBG};
  font-size: ${({ theme }) => theme.fontSizes.small};
  z-index: 2;
  }

  ul {
    padding: 0;
    margin: 0;
    li {
      padding: 10px;
      list-style: none;
      font-size: ${({ theme }) => theme.fontSizes.small};
      transition: all 200ms ease;
      &:hover {
        background: ${({ theme }) => theme.primaryButtonHover};
        cursor: pointer;
      }
    }
  }
`;

const PresetFilterWrapper = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_md")};
  position: relative;
`;

type FilterFlyBarProps = {
  resetOffset?: React.Dispatch<React.SetStateAction<number>>;
  customLabels: TagClassificationConfig[];
  handleShowFilters: (show: boolean) => void;
  show: boolean;
  resetPage?: (search: string) => void;
  selectedPreset: IPresetDataItem | undefined;
  presetFilterData: IPresetFilter | undefined;
  mutatePresetFilter: () => void;
  handleActivePresetId: (
    id: string,
    isPresetSelected: boolean
  ) => Promise<void>;
  clearFilter: (preventPresetActivation: boolean) => void;
};

export function FiltersFlyBar({
  resetOffset = noop,
  customLabels,
  handleShowFilters,
  show,
  resetPage,
  selectedPreset,
  presetFilterData,
  mutatePresetFilter,
  handleActivePresetId,
  clearFilter,
}: FilterFlyBarProps) {
  // "ArrayParam" is how useQueryParams handles things like
  // ?market_segment=abc&market_segment=xyz
  // That URL will become ["abc", "xyz"]

  const [filtersFromUrl, setFiltersFromUrl] =
    useState<{ [key: string]: string[] }>();

  const { search } = useLocation();
  const history = useHistory();
  const { roleIsGuest, roleIsSomeKindOfSeller } = useAuthContext();
  const { notifyError, notifySuccess } = useContext(Notifications);
  const [pageFilters, setPageFilters] = useState<Filters>();
  const [searchResults, setSearchResults] = useState<IsearchFilterItem[]>();
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showPresetFilter, setShowPresetFilter] = useState(false);
  const [filteredItems, setFilteredItems] = useState<IFilters>({});
  const [isUpdatePreset, setIsUpdatePreset] = useState(false);
  const [showPresetDialog, setShowPresetDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const { storefront_id, slug: tenantSlug, tenant_id } = useStoreState();

  const [filtersSearchQuery, setFiltersSearchQuery] = useState("");

  const queryObject = () => {
    const querylist: any = {
      active_tag_view: StringParam,
      q: StringParam,
    };
    if (pageFilters) {
      for (const [key] of Object.entries(pageFilters)) {
        querylist[key] = ArrayParam;
      }
    }
    return querylist;
  };

  const [query, setQuery] = useQueryParams(queryObject());

  const { t } = useTranslation();

  const [cookies] = useCookies([`preferred-language-${tenantSlug}`]);

  const preferredLanguage: SupportedLanguage | undefined = cookies[
    `preferred-language-${tenantSlug}`
  ] as SupportedLanguage;

  useEffect(() => {
    const params = new URLSearchParams(search.substring(1));
    const filters = groupPortfolioParamsByKey(params);
    setFiltersFromUrl(filters);

    const filteredKeys = Object.keys(filters).filter(
      (key) =>
        key !== "perPage" && key !== "offset" && key !== "active_tag_view"
    );
    const hasValidFilters = filteredKeys.some(
      (key) => Array.isArray(filters[key]) && filters[key].length > 0
    );
    setShowPresetFilter(hasValidFilters);
  }, [search]);

  const { data, error: filtersError } = useSWR<ProductFiltersMainSchema, Error>(
    [
      endpoints.v2_storefronts_id_products_filters(storefront_id),
      React.useMemo(
        () => ({
          params: applyFiltersToURL({
            filtersFromUrl: {
              show_inactive: ["true"],
              ...(filtersFromUrl ?? {}),
            },
            params: new URLSearchParams(),
          }),
        }),
        [filtersFromUrl]
      ),
    ]
  );
  const filters = data?.filters;

  // Status filters for seller_admin
  const { data: statusData } = useSWR<ProductStatusesSchema, Error>(
    roleIsSomeKindOfSeller && tenant_id
      ? endpoints.v2_tenants_tenant_id_pim_products_status(tenant_id)
      : null
  );
  const statuses = statusData?.statuses;

  // Template filters for seller_admin with view_templates permission
  const { data: templateData } = useSWR<
    WithPagination<{ data: AttributeTemplateSummarySchema[] }>,
    Error
  >(
    roleIsSomeKindOfSeller && tenant_id
      ? `${endpoints.v2_storefronts_id_pim_templates_summary(
          tenant_id
        )}?minimum_number_of_products=1&limit=100`
      : null
  );

  const handleFilter = ({
    values,
    filter,
  }: {
    values?: ChipType[];
    filter: string;
  }) => {
    if (values) {
      const filtersArray = chipsToStringArray(values);
      const processedFiltersArray =
        filtersArray.length > 0 && filter === "status"
          ? filtersArray.map((filterValue) =>
              getProductStatusValue(filterValue, t)
            )
          : filtersArray;

      if (resetPage) {
        const updatedQuery = query;
        for (const [key, value] of Object.entries(updatedQuery)) {
          if (!value) {
            delete updatedQuery[key];
          }
        }
        resetPage(
          `?${stringify({
            active_tag_view: filter,
            ...updatedQuery,
            ...(processedFiltersArray.length > 0
              ? { [filter]: processedFiltersArray }
              : {}),
          })}`
        );
      }
    } else {
      if (resetPage) {
        const updatedQuery = query;
        for (const [key, value] of Object.entries(updatedQuery)) {
          if (!value) {
            delete updatedQuery[key];
          }
        }
        delete updatedQuery[filter];
        if (!query.q) {
          delete updatedQuery.q;
        }
        resetPage(`?${stringify(updatedQuery)}`);
      }
    }
    resetOffset(0);
  };

  const handleChange = (val: React.ChangeEvent<HTMLInputElement>) => {
    if (val.target.value) {
      setShowSearchResults(true);
    }
    setFiltersSearchQuery(val.target.value);
  };

  const handleClearInput = () => setFiltersSearchQuery("");

  const getFilterName = (filter: IsearchFilterItem) => {
    if (
      filtersSearchQuery.length > 0 &&
      filter.label.toLowerCase().indexOf(filtersSearchQuery.toLowerCase()) > -1
    ) {
      const start = filter.label
        .toLowerCase()
        .indexOf(filtersSearchQuery.toLowerCase());
      const end =
        filter.label.toLowerCase().indexOf(filtersSearchQuery.toLowerCase()) +
        filtersSearchQuery.length;
      return (
        <>
          {filter.label.substring(0, start)}
          <b>{filter.label.substring(start, end)}</b>
          {filter.label.substring(end, filter.label.length)}
        </>
      );
    }
    return filter.label;
  };

  const handleFilterClick = (filter: { type: string; label: string }) => {
    // @ts-ignore
    if (!query[filter.type]?.includes(filter.label)) {
      setQuery({
        [filter.type]:
          filtersFromUrl && filtersFromUrl[filter.type]
            ? [
                ...filtersFromUrl[filter.type],
                filter.type === "status"
                  ? getProductStatusValue(filter.label, t)
                  : filter.label,
              ]
            : [
                filter.type === "status"
                  ? getProductStatusValue(filter.label, t)
                  : filter.label,
              ],
      });
      resetOffset(0);
    }
  };

  const wrapperRef = useRef(document.createElement("div"));

  useEffect(() => {
    const handleClickOutsideSearchResult = (event: any) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        showSearchResults
      ) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("click", handleClickOutsideSearchResult, false);
    return () => {
      document.removeEventListener(
        "click",
        handleClickOutsideSearchResult,
        false
      );
    };
  }, [showSearchResults]);

  useEffect(() => {
    if (filters) {
      const filters_by_custom_labels: Filters = {};

      // First, process all custom labels (including status and template if they come from API)
      customLabels.forEach(({ filter_type }) => {
        if (filters[filter_type] && filters[filter_type].length > 0) {
          filters_by_custom_labels[filter_type] = filters[filter_type];
        }
      });

      // Add status filters from API if available, otherwise create from status data for seller_admin
      if (filters.status && filters.status.length > 0) {
        // Use status filters from API but ensure they show translated text
        filters_by_custom_labels.status = filters.status.map((item) => ({
          ...item,
          name: getProductStatusText(item.name as ProductStatusType, t),
        }));
      } else if (roleIsSomeKindOfSeller && statuses) {
        // Fallback: create status filters from status data (counts will be 0)
        filters_by_custom_labels.status = (statuses as string[]).map(
          (item) => ({
            name: getProductStatusText(item as ProductStatusType, t),
            count: 0,
            image_url: null,
            selected: false,
          })
        );
      }

      // Add template filters from API if available, otherwise create from template data for seller_admin
      if (filters.template && filters.template.length > 0) {
        // Use template filters from API (they have counts)
        filters_by_custom_labels.template = filters.template;
      } else if (roleIsSomeKindOfSeller && templateData) {
        // Fallback: create template filters from template data (counts will be 0)
        filters_by_custom_labels.template = templateData.data.map((item) => ({
          id: item.template_id,
          name: item.template_name,
          count: 0,
          image_url: null,
          selected: false,
        }));
      }

      setPageFilters(filters_by_custom_labels);
    }
  }, [
    customLabels,
    filters,
    statuses,
    templateData,
    t,
    roleIsSomeKindOfSeller,
  ]);

  useEffect(() => {
    let filtersList: IsearchFilterItem[] = [];
    if (pageFilters) {
      for (const [key, value] of Object.entries(pageFilters)) {
        value.forEach((item) => {
          if (!query[key]?.includes(item.name)) {
            filtersList.push({ type: key, label: item.name });
          }
        });
      }
      setSearchResults(
        filtersList.filter(
          (item) =>
            item.label
              .toLocaleLowerCase()
              .indexOf(filtersSearchQuery.toLocaleLowerCase()) > -1
        )
      );
    }
  }, [pageFilters, filtersSearchQuery, query]);

  const handleSetFiltersInUrl = React.useCallback(
    (filters: IFilters) => {
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, items]) => {
        items.forEach((item) => {
          params.append(key, item.name);
        });
      });

      const newUrl = `?${params.toString().replace(/\+/g, "%20")}`;
      history.push({ search: newUrl });
    },
    [history]
  );

  useEffect(() => {
    if (!pageFilters || !filtersFromUrl) return;

    const { offset, perPage, active_tag_view, ...validFilters } =
      filtersFromUrl;
    const results: IFilters = {};

    for (const key in validFilters) {
      const filterValues = validFilters[key];
      const mainList = pageFilters[key] || [];

      if (!Array.isArray(filterValues) || !Array.isArray(mainList)) continue;

      if (key === "status") {
        // Special handling for status filters
        const matched = mainList
          .filter((item) => {
            return filterValues.some((filterValue: string) => {
              const productStatusText = getProductStatusText(
                filterValue as ProductStatusType,
                t
              );
              return item.name === productStatusText;
            });
          })
          .map((item) => ({
            id: item.id ? String(item.id) : null,
            name: item.name, // Keep the translated display name
          }));
        results[key] = matched;
      } else {
        const matched = mainList
          .filter((item) => filterValues.includes(item.name))
          .map((item) => ({
            id: item.id ? String(item.id) : null,
            name: item.name,
          }));
        results[key] = matched;
      }
    }

    setFilteredItems(results);
  }, [pageFilters, filtersFromUrl, t]);

  const getNormalizedFilterItems = (filter: IFilters): string[] => {
    return Object.values(filter)
      .reduce((acc, arr) => acc.concat(arr), [])
      .map((item) => item.name.toLocaleLowerCase());
  };

  const areArraysEqual = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
  };

  useEffect(() => {
    const filter = presetFilterData?.data?.[0]?.filters;

    if (!filter) {
      setIsUpdatePreset(true);
      return;
    }

    if (filter && filteredItems) {
      const ids = getNormalizedFilterItems(filter);
      const cleanFilteredItems = getNormalizedFilterItems(filteredItems);

      const result = areArraysEqual(cleanFilteredItems, ids);
      setIsUpdatePreset(result);
    }
  }, [filteredItems, presetFilterData]);

  useEffect(() => {
    const filters = presetFilterData?.data?.[0]?.filters;
    const hasPresetSelected = presetFilterData?.data?.[0]?.selected;

    if (!filters) return;

    const presetFilterIds = getNormalizedFilterItems(filters);

    const urlParams = new URLSearchParams(window.location.search);
    const parsedFiltersFromUrl = groupPortfolioParamsByKey(urlParams);

    const { active_tag_view, ...remainingFilters } = parsedFiltersFromUrl;
    const hasNoFilters = Object.keys(remainingFilters).length === 0;

    const normalizeUrlFilters = (filters: {
      [key: string]: string[];
    }): string[] => {
      return Object.values(filters)
        .flat()
        .map((filterValue) => filterValue.toLowerCase().trim());
    };

    const normalizedFiltersFromUrl = normalizeUrlFilters(remainingFilters);

    const filtersMatch = areArraysEqual(
      normalizedFiltersFromUrl,
      presetFilterIds
    );
    const shouldApplyFilters = hasNoFilters || filtersMatch;

    if (hasPresetSelected && filters && shouldApplyFilters) {
      handleSetFiltersInUrl(filters);
    }
  }, [presetFilterData, handleSetFiltersInUrl]);

  const shouldShowPresetFilter =
    (showPresetFilter || (presetFilterData?.data?.length ?? 0) > 0) &&
    !roleIsGuest;

  const handleSubmitPreset = async (preset_name: string) => {
    setShowPresetDialog(false);
    const presetId = presetFilterData?.data?.[0]?.id || "";
    try {
      const request = isEditMode
        ? axios.patch(
            endpoints.v2_storefronts_id_user_filters_userFilterId(
              storefront_id,
              presetId
            ),
            { name: preset_name }
          )
        : axios.post(endpoints.v2_storefronts_id_user_filters(storefront_id), {
            name: preset_name,
            filters: filteredItems,
            selected: true,
          });

      const response = await request;

      if (response.status === 201) {
        setIsEditMode(false);
        mutatePresetFilter();
        notifySuccess(
          t(isEditMode ? "Preset updated" : "New Preset saved") as string
        );
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  const handleDeletePreset = async (id: string) => {
    try {
      const response = await axios.delete(
        endpoints.v2_storefronts_id_user_filters_userFilterId(storefront_id, id)
      );
      if (response.status === 204) {
        mutatePresetFilter();
        notifySuccess(t("Preset deleted") as string);
        clearFilter(true);
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  const handleUpdatePreset = async () => {
    const presetId = selectedPreset?.id || "";
    try {
      const response = await axios.patch(
        endpoints.v2_storefronts_id_user_filters_userFilterId(
          storefront_id,
          presetId
        ),
        {
          filters: filteredItems,
        }
      );
      if (response.status === 201) {
        mutatePresetFilter();
        notifySuccess(t("Preset updated") as string);
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  const handleApplyPreset = async (
    id: string,
    setShowDropdown: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    const filters = presetFilterData?.data?.[0]?.filters;
    if (filters) {
      await handleActivePresetId(id, true);
      handleSetFiltersInUrl(filters);
      setShowDropdown(false);
    }
  };

  // Could potentially display an error notification after multiple failed
  // attempts. That is not yet set up in this project.
  if (filtersError) {
    // Search still works if filters don't load.
    // This maintains the layout of the page in case of error.
    return <EmptyFiltersPlaceholder />;
  } else {
    // Handle case that happened once on staging where all filters were empty arrays.
    // This might theoretically be possible if a tenant is activated and no filters
    // are added.

    return (
      <FiltersFlyBarWrapper show={show}>
        {show && (
          <>
            <FiltersHeaderWrapper>
              <FiltersHeader>
                <span>{t("Filters")}</span>
                <InvisibleButton onClick={() => handleShowFilters(false)}>
                  <XIcon width={16} height={16} />
                </InvisibleButton>
              </FiltersHeader>
              <SearchBarWrapper ref={wrapperRef}>
                <SearchBar
                  placeHolder={t("Search for filters")}
                  query={filtersSearchQuery}
                  handleChange={handleChange}
                  handleClearInput={handleClearInput}
                  onFocus={() => {
                    if (searchResults?.length && filtersSearchQuery.length) {
                      setShowSearchResults(true);
                    }
                  }}
                />
                {showSearchResults && filtersSearchQuery.length > 0 && (
                  <SearchResultsWrapper>
                    <ul>
                      {searchResults?.map((result) => (
                        <li
                          onClick={() => {
                            handleFilterClick(result);
                          }}
                        >
                          {getCustomLabel({
                            filter_type: result.type,
                            tag_classification_configs: customLabels,
                            preferred_language: preferredLanguage,
                          })}{" "}
                          {` > `}
                          {getFilterName(result)}
                        </li>
                      ))}
                    </ul>
                  </SearchResultsWrapper>
                )}
              </SearchBarWrapper>
              {shouldShowPresetFilter && (
                <PresetFilterWrapper>
                  <PresetFilter
                    dropdownWidth="100%"
                    presetFilterData={presetFilterData}
                    isUpdatePreset={isUpdatePreset}
                    selectedPreset={selectedPreset}
                    showPresetDialog={showPresetDialog}
                    setShowPresetDialog={setShowPresetDialog}
                    setIsEditMode={setIsEditMode}
                    onSubmitPreset={handleSubmitPreset}
                    onDeletePreset={handleDeletePreset}
                    onApplyPreset={handleApplyPreset}
                    onUpdatePreset={handleUpdatePreset}
                  />
                </PresetFilterWrapper>
              )}
            </FiltersHeaderWrapper>

            <FiltersWrapper>
              {pageFilters &&
                Object.entries(pageFilters).map((FilterItem, index) => {
                  return (
                    <FilterGroup
                      key={FilterItem[0]}
                      setter={handleFilter}
                      options={FilterItem[1]}
                      filters={pageFilters}
                      customLabels={customLabels}
                      expanded={
                        (index === 0 && !query.active_tag_view) ||
                        query.active_tag_view === FilterItem[0]
                      }
                      filterType={FilterItem[0]}
                      preselectedFilters={
                        FilterItem[0] === "status" &&
                        filteredItems[FilterItem[0]]
                          ? filteredItems[FilterItem[0]].map((item) => ({
                              name: item.name,
                            }))
                          : convertToChipArray(
                              filtersFromUrl
                                ? filtersFromUrl[FilterItem[0]]
                                : []
                            ) ?? []
                      }
                    />
                  );
                })}
            </FiltersWrapper>
          </>
        )}
      </FiltersFlyBarWrapper>
    );
  }
}
